import {
  ArrayMaxSize,
  ArrayMinSize,
  IsArray,
  IsBoolean,
  IsDateString,
  IsEmail,
  IsIn,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
  MinLength,
} from 'class-validator';
import type { CommitteeDecision } from './stage-data.dto';

/**
 * Appeals to the Appeal Committee (LGA s. 134) and the committee's hearings.
 *
 * The lifecycle:
 *
 *   submitted ─accept→ accepted ─schedule→ scheduled ─record→ heard ─decide→ decided
 *       └─reject→ rejected        (withdrawn from any state before decided)
 *
 * An owner's appeal from the Client Portal arrives `submitted` and waits for
 * staff to accept it onto the record; a staff-recorded appeal is `accepted` at
 * once. Accepting is what writes `appealFiled` into the file's Step 6. A
 * hearing can be adjourned and re-scheduled any number of times; the decision
 * is recorded once and is final.
 */

export type AppealSource = 'owner' | 'staff';
export type AppealStatus =
  'submitted' | 'accepted' | 'rejected' | 'scheduled' | 'heard' | 'decided' | 'withdrawn';
export const APPEAL_STATUSES: readonly AppealStatus[] = [
  'submitted',
  'accepted',
  'rejected',
  'scheduled',
  'heard',
  'decided',
  'withdrawn',
];

export type AppellantRole = 'owner' | 'occupant' | 'agent';
export const APPELLANT_ROLES: readonly AppellantRole[] = ['owner', 'occupant', 'agent'];

/** What the committee did with the notice. */
export type AppealDecision = 'upheld' | 'varied' | 'quashed';
export const APPEAL_DECISIONS: readonly AppealDecision[] = ['upheld', 'varied', 'quashed'];

/** For a varied notice: were its terms changed, or only its time extended? */
export type AppealVariation = 'modified' | 'extended';
export const APPEAL_VARIATIONS: readonly AppealVariation[] = ['modified', 'extended'];

export type HearingStatus = 'scheduled' | 'held' | 'adjourned' | 'cancelled';

/** States an appeal can no longer move out of. */
export const TERMINAL_APPEAL_STATUSES: readonly AppealStatus[] = [
  'rejected',
  'decided',
  'withdrawn',
];

/** States in which the appeal is on the record and before the committee. */
export const LIVE_APPEAL_STATUSES: readonly AppealStatus[] = ['accepted', 'scheduled', 'heard'];

/**
 * The Step 6 decision a committee outcome is recorded as.
 *
 * Step 6 predates this module and speaks the statute's verbs (confirm / modify
 * / extend / rescind); the appeal speaks the committee's (upheld / varied /
 * quashed). This is the one place the two are translated, so the workflow,
 * the deadlines engine and the documents keep reading the vocabulary they
 * always have.
 */
export function committeeDecisionFor(
  decision: AppealDecision,
  variation: AppealVariation | null | undefined,
): CommitteeDecision {
  if (decision === 'upheld') return 'confirm';
  if (decision === 'quashed') return 'rescind';
  return variation === 'extended' ? 'extend' : 'modify';
}

/** True when a decision needs a new date to comply by. */
export function decisionNeedsComplianceDate(decision: AppealDecision): boolean {
  return decision === 'varied';
}

export interface HearingMemberDto {
  councilMemberId: string | null;
  name: string;
  titleEn: string;
  titleFr: string;
  role: 'chair' | 'member';
}

export interface AppealHearingDto {
  id: string;
  appealId: string;
  scheduledFor: string;
  location: string;
  members: HearingMemberDto[];
  status: HearingStatus;
  noticeSentAt: string | null;
  heldAt: string | null;
  appellantAttended: boolean | null;
  attendees: string | null;
  minutes: string | null;
  createdAt: string;
}

export interface AppealDto {
  id: string;
  fileId: string;
  sequence: number;
  source: AppealSource;
  status: AppealStatus;
  appellantName: string;
  appellantRole: AppellantRole;
  appellantEmail: string | null;
  appellantPhone: string | null;
  appellantLocale: 'en' | 'fr';
  grounds: string;
  reliefSought: string | null;
  filedAt: string;
  withinWindow: boolean | null;
  receivedByName: string | null;
  rejectionReason: string | null;
  withdrawnAt: string | null;
  decision: AppealDecision | null;
  decisionVariation: AppealVariation | null;
  decisionDate: string | null;
  decisionReasons: string | null;
  revisedComplianceDeadline: string | null;
  decidedByName: string | null;
  hearings: AppealHearingDto[];
  createdAt: string;
  updatedAt: string;
}

/** `GET /files/:fileId/appeals`. */
export interface FileAppealsDto {
  /** Whether the module is on at this file's municipality. Off, the rest is empty. */
  enabled: boolean;
  /** Step 5 service date, the start of the 14-day window; null until served. */
  serviceDate: string | null;
  /** Last day an appeal is in time (inclusive); null until served. */
  windowClosesOn: string | null;
  appeals: AppealDto[];
}

/** An official the committee can be drawn from: the municipality's roster. */
export interface CommitteeCandidateDto {
  id: string;
  name: string;
  titleEn: string;
  titleFr: string;
}

/** `POST /files/:fileId/appeals` — staff recording an appeal received by letter, email or in person. */
export class RecordAppealDto {
  @IsString()
  @MinLength(1)
  @MaxLength(200)
  appellantName!: string;

  @IsIn(APPELLANT_ROLES)
  appellantRole!: AppellantRole;

  @IsOptional()
  @IsEmail()
  @MaxLength(320)
  appellantEmail?: string;

  @IsOptional()
  @IsString()
  @MaxLength(32)
  appellantPhone?: string;

  @IsIn(['en', 'fr'])
  appellantLocale!: 'en' | 'fr';

  @IsString()
  @MinLength(1)
  @MaxLength(10000)
  grounds!: string;

  @IsOptional()
  @IsString()
  @MaxLength(4000)
  reliefSought?: string;

  /** When the appeal reached the municipality. Defaults to now. */
  @IsOptional()
  @IsDateString()
  filedAt?: string;
}

/** `POST /files/:fileId/appeals/:appealId/accept`. */
export class AcceptAppealDto {}

/** `POST /files/:fileId/appeals/:appealId/reject` — the owner reads the reason. */
export class RejectAppealDto {
  @IsString()
  @MinLength(1)
  @MaxLength(2000)
  reason!: string;
}

/** `POST /files/:fileId/appeals/:appealId/withdraw`. */
export class WithdrawAppealDto {
  @IsOptional()
  @IsString()
  @MaxLength(2000)
  note?: string;
}

/** `POST /files/:fileId/appeals/:appealId/hearings`. */
export class ScheduleHearingDto {
  @IsDateString()
  scheduledFor!: string;

  @IsString()
  @MinLength(1)
  @MaxLength(400)
  location!: string;

  /** Officials from the municipality's roster who will sit. */
  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(15)
  @IsUUID('all', { each: true })
  memberIds!: string[];

  /** Which of `memberIds` chairs. */
  @IsUUID()
  chairId!: string;

  /** Email the notice of hearing to the appellant (default true). */
  @IsOptional()
  @IsBoolean()
  notifyAppellant?: boolean;
}

/** `POST /files/:fileId/appeals/:appealId/hearings/:hearingId/record`. */
export class RecordHearingDto {
  @IsIn(['held', 'adjourned', 'cancelled'])
  outcome!: 'held' | 'adjourned' | 'cancelled';

  @IsOptional()
  @IsDateString()
  heldAt?: string;

  @IsOptional()
  @IsBoolean()
  appellantAttended?: boolean;

  @IsOptional()
  @IsString()
  @MaxLength(4000)
  attendees?: string;

  @IsOptional()
  @IsString()
  @MaxLength(20000)
  minutes?: string;
}

/** `POST /files/:fileId/appeals/:appealId/decision`. */
export class DecideAppealDto {
  @IsIn(APPEAL_DECISIONS)
  decision!: AppealDecision;

  @IsOptional()
  @IsIn(APPEAL_VARIATIONS)
  variation?: AppealVariation;

  @IsDateString()
  decisionDate!: string;

  @IsString()
  @MinLength(1)
  @MaxLength(10000)
  reasons!: string;

  /** Required when `decision` is `varied`. */
  @IsOptional()
  @IsDateString()
  revisedComplianceDeadline?: string;

  /** Email the decision to the appellant (default true). */
  @IsOptional()
  @IsBoolean()
  notifyAppellant?: boolean;
}

/** Codes the appeals endpoints refuse with, for the client to translate. */
export type AppealErrorCode =
  | 'module_disabled'
  | 'notice_not_served'
  | 'appeal_open'
  | 'appeal_state'
  | 'hearing_state'
  | 'member_not_official'
  | 'compliance_date_required'
  | 'decision_before_hearing';

// ── Client Portal ─────────────────────────────────────────────────────────

/**
 * An appeal as its appellant sees it. No staff names, no hearing record —
 * only what was filed, where it stands, when and where the hearing is, and
 * the decision with its reasons.
 */
export interface CitizenAppealDto {
  id: string;
  sequence: number;
  status: AppealStatus;
  grounds: string;
  reliefSought: string | null;
  filedAt: string;
  rejectionReason: string | null;
  nextHearing: { scheduledFor: string; location: string } | null;
  decision: AppealDecision | null;
  decisionVariation: AppealVariation | null;
  decisionDate: string | null;
  decisionReasons: string | null;
  revisedComplianceDeadline: string | null;
}

/** `GET /portal/files/:id/appeals`. */
export interface CitizenAppealsDto {
  enabled: boolean;
  /** True when a notice has been served and no appeal is already open. */
  canFile: boolean;
  windowClosesOn: string | null;
  appeals: CitizenAppealDto[];
}

/** `POST /portal/files/:id/appeals`. */
export class CitizenFileAppealDto {
  @IsIn(APPELLANT_ROLES)
  appellantRole!: AppellantRole;

  @IsString()
  @MinLength(10)
  @MaxLength(10000)
  grounds!: string;

  @IsOptional()
  @IsString()
  @MaxLength(4000)
  reliefSought?: string;

  @IsOptional()
  @IsString()
  @MaxLength(32)
  phone?: string;

  @IsIn(['en', 'fr'])
  locale!: 'en' | 'fr';
}
